import React from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_INFO, COMPANY_INFO } from '../../utils/constants';
import './ServicePage.css';

const ThreeTonTruck: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "3 Ton Pickup Truck Rental Dubai",
    "description": "Professional 3 ton pickup truck rental service in Dubai with tail lift for medium-sized moves, deliveries, and transportation needs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "telephone": CONTACT_INFO.phone,
      "email": CONTACT_INFO.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": CONTACT_INFO.address,
        "addressLocality": "Dubai",
        "addressCountry": "UAE"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Dubai"
    }
  };

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero">
        <div className="container">
          <div className="hero-content">
            <h1>3 Ton Pickup Truck Rental Dubai</h1>
            <p>Ideal for medium-sized moves, deliveries, and transportation needs across Dubai with professional service</p>
            <div className="hero-buttons">
              <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call Now: {CONTACT_INFO.phone}</a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Get Quote</a>
            </div>
          </div>
          <div className="hero-image">
            <img src="/3-ton-pickup-rental-truck-dubai.png" alt="3 Ton Pickup Truck Dubai" />
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="service-details">
        <div className="container">
          <div className="service-overview">
            <h2>3 Ton Pickup Truck Rental Service in Dubai</h2>
            <p>
              Our 3 ton pickup truck rental service in Dubai is perfect for medium-sized businesses, 
              residential moves, and commercial deliveries. With enhanced capacity and professional 
              drivers, we ensure efficient transportation of your goods across Dubai's busy streets. 
              For smaller needs, check our <Link to="/services/1-ton-truck">1 ton pickup truck</Link> service, 
              or for larger requirements, consider our <Link to="/services/5-ton-truck">5 ton pickup truck</Link> or <Link to="/services/7-ton-truck">7 ton pickup truck</Link> services.
            </p>
          </div>

          <div className="service-features">
            <h3>Key Features of Our 3 Ton Pickup Trucks</h3>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">🚛</div>
                <h4>Tail Lift Available</h4>
                <p>Hydraulic tail lift system for easy loading and unloading of medium-sized items</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📦</div>
                <h4>Open & Box Options</h4>
                <p>Flexible cargo options with both open bed and covered box configurations</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">⏰</div>
                <h4>24/7 Service</h4>
                <p>Round-the-clock availability for urgent transportation needs</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👨‍💼</div>
                <h4>Professional Driver</h4>
                <p>Experienced drivers with comprehensive knowledge of Dubai's routes</p>
              </div>
            </div>
          </div>

          <div className="service-applications">
            <h3>Perfect For</h3>
            <div className="applications-grid">
              <div className="application-item">
                <h4>🏠 Residential Moving</h4>
                <p>Ideal for moving medium-sized furniture, appliances, and household items</p>
              </div>
              <div className="application-item">
                <h4>🏢 Office Relocation</h4>
                <p>Perfect for small to medium office moves and equipment transportation</p>
              </div>
              <div className="application-item">
                <h4>🛒 Commercial Deliveries</h4>
                <p>Efficient for retail deliveries, restaurant supplies, and commercial goods</p>
              </div>
              <div className="application-item">
                <h4>🏗️ Construction Materials</h4>
                <p>Great for transporting building materials, tools, and construction supplies</p>
              </div>
            </div>
          </div>

          <div className="service-areas">
            <h3>Service Areas in Dubai</h3>
            <p>We provide 3 ton pickup truck rental services across all major areas of Dubai:</p>
            <div className="areas-list">
              <div className="area-group">
                <h4>Central Dubai</h4>
                <p>Downtown Dubai, DIFC, Business Bay, Jumeirah, Marina, DSO</p>
              </div>
              <div className="area-group">
                <h4>Northern Emirates</h4>
                <p>Deira, Bur Dubai, Karama, Satwa, Al Quoz, Mirdif</p>
              </div>
              <div className="area-group">
                <h4>New Dubai</h4>
                <p>Dubai Hills, Dubai Sports City, JVC, JLT, Motor City, Arabian Ranches</p>
              </div>
              <div className="area-group">
                <h4>Industrial Areas</h4>
                <p>Al Quoz Industrial, Jebel Ali, DIP, DIC, DWC, Silicon Oasis</p>
              </div>
            </div>
          </div>

          <div className="pricing-info">
            <h3>Competitive Pricing</h3>
            <p>
              Our 3 ton pickup truck rental rates are competitive and transparent. We offer flexible 
              pricing options including hourly, daily, and weekly rentals. Contact us for a detailed 
              quote based on your specific requirements.
            </p>
            <div className="pricing-features">
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>No hidden charges</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Fuel included</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Driver included</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Insurance covered</span>
              </div>
            </div>
          </div>

          <div className="booking-process">
            <h3>How to Book</h3>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h4>Contact Us</h4>
                <p>Call or email us with your pickup and delivery details</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h4>Get Quote</h4>
                <p>Receive a detailed quote with pricing and timing</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h4>Confirm Booking</h4>
                <p>Confirm your booking and receive confirmation details</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h4>Service Delivery</h4>
                <p>Our driver arrives on time with the 3 ton pickup truck</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Book Your 3 Ton Pickup Truck?</h2>
            <p>Contact us now for reliable and professional 3 ton pickup truck rental services in Dubai</p>
            <div className="cta-buttons">
              <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call: {CONTACT_INFO.phone}</a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Email Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="related-services">
        <div className="container">
          <h3>Other Truck Rental Services</h3>
          <div className="services-grid">
            <Link to="/services/1-ton-truck" className="service-card">
              <img src="/pickup-rental-truck-dubai-home1_210x160.jpeg" alt="1 Ton Pickup Truck" />
              <h4>1 Ton Pickup Truck</h4>
              <p>Perfect for small deliveries and light moving tasks</p>
            </Link>
            <Link to="/services/5-ton-truck" className="service-card">
              <img src="/pickup-rental-truck-dubai-home8_210x160.jpeg" alt="5 Ton Pickup Truck" />
              <h4>5 Ton Pickup Truck</h4>
              <p>Great for larger moves and heavy deliveries</p>
            </Link>
            <Link to="/services/7-ton-truck" className="service-card">
              <img src="/pickup-rental-truck-dubai-home10_210x160.jpeg" alt="7 Ton Pickup Truck" />
              <h4>7 Ton Pickup Truck</h4>
              <p>Perfect for office moves and large deliveries</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <section className="related-blog-posts">
        <div className="container">
          <h3>Related Articles</h3>
          <div className="blog-posts-grid">
            <Link to="/pickup-truck-rental-guide-dubai" className="blog-post-card">
              <div className="blog-post-content">
                <h4>The Ultimate Guide to Pickup Truck Rental in Dubai</h4>
                <p>Complete guide to renting pickup trucks in Dubai. Learn about truck sizes, pricing, and requirements.</p>
                <span className="read-more">Read More →</span>
              </div>
            </Link>
            <Link to="/best-movers-packers-dubai-2025" className="blog-post-card">
              <div className="blog-post-content">
                <h4>Best Movers and Packers in Dubai 2025</h4>
                <p>Discover the top-rated movers and packers in Dubai offering professional home shifting services.</p>
                <span className="read-more">Read More →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThreeTonTruck;
